<!--
  =====================================================================
  SECTION BANNERS - CAROUSEL WITH SHUFFLE & NAVIGATION
  =====================================================================
  
  Responsive banner carousel component with drag/touch support.
  
  FEATURES:
  - Automatic banner shuffle on load
  - Horizontal carousel with drag-to-scroll
  - Touch and mouse support (swipe on mobile)
  - Responsive card sizing (1/2/3 columns)
  - Dot navigation for carousel control
  - Arrow navigation buttons
  - Click prevention on drag
  - Page detection for visibility
  
  BEHAVIOR:
  - Desktop (>480px): Multiple cards visible simultaneously
  - Mobile (≤480px): Single card visible (swipe to navigate)
  - Banners shuffle randomly on each load
  - Carousel position resets when banners update
  - Auto-fit to container dimensions
  
  =====================================================================
-->

<template>
  <section v-if="shouldShowBanners" class="section-banners">
    <div class="container">
      <div class="carousel-container">
        
        <button 
          v-show="canScrollLeft"
          class="arrow arrow-left" 
          @click="scrollPrev"
          aria-label="Scorri a sinistra"
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
            <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>

        <button 
          v-show="canScrollRight"
          class="arrow arrow-right" 
          @click="scrollNext"
          aria-label="Scorri a destra"
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
            <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>

        <div
          class="carousel-viewport"
          ref="viewport"
          :class="{ 'is-dragging': isDragging }"
          @pointerdown="onPointerDown"
          @pointermove="onPointerMove"
          @pointerup="onPointerUp"
          @pointercancel="onPointerUp"
        >
          <div class="carousel-track" ref="track" @dragstart.prevent>
            
            <div
              v-for="(banner, index) in banners"
              :key="index"
              class="banner-card"
            >
              <a
                :href="banner.link || '#'"
                class="banner-link"
                :target="banner.link?.startsWith('http') ? '_blank' : '_self'"
                :rel="banner.link?.startsWith('http') ? 'noopener noreferrer' : ''"
                @click="handleBannerClick($event, banner)"
                draggable="false"
              >
                <div class="banner-image-wrapper">
                  <img 
                    :src="banner.image" 
                    :alt="(banner.title || banner.label) ? '' : 'Banner promozionale'" 
                    class="banner-image"
                    draggable="false"
                  />
                  <div class="banner-overlay"></div>
                  <div class="banner-text">
                    <span class="banner-title">{{ banner.title || banner.label || '' }}</span>
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>

        <div v-if="totalDots > 1" class="dots-navigation">
          <button
            v-for="(item, index) in totalDots"
            :key="index"
            class="dot"
            :class="{ active: currentDotIndex === index }"
            @click="goToDot(index)"
            :aria-label="`Vai al gruppo ${index + 1}`"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue'
import { useCarouselScroll } from '@/composables/useCarouselScroll.js'
import { getBanners, hasBanners } from '@/store/index.js'

// ───────────────────────────────────────────────────────────────────
// SHUFFLE UTILITY
// ───────────────────────────────────────────────────────────────────

/**
 * Shuffles array using Fisher-Yates algorithm
 * @param {Array} array - Array to shuffle
 * @returns {Array} Shuffled copy of array
 */
function shuffleArray(array) {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

// ───────────────────────────────────────────────────────────────────
// REACTIVE STATE - BANNERS & DATA
// ───────────────────────────────────────────────────────────────────

const originalBanners = computed(() => getBanners())
const shuffledBannersList = ref([])
const banners = computed(() => shuffledBannersList.value)

// ───────────────────────────────────────────────────────────────────
// VISIBILITY DETECTION
// ───────────────────────────────────────────────────────────────────

/**
 * Determines if banners should be shown
 * Only shows on homepage or root page
 */
const shouldShowBanners = computed(() => {
  const path = (window.location?.pathname || '/').replace(/\/+$/, '')
  const segments = path.split('/fondazione/').filter(Boolean)
  return hasBanners() && segments.length <= 2
})

// ───────────────────────────────────────────────────────────────────
// REACTIVE STATE - CAROUSEL DIMENSIONS
// ───────────────────────────────────────────────────────────────────

const {
  viewport,
  track,
  isDragging,
  totalDots,
  currentDotIndex,
  canScrollLeft,
  canScrollRight,
  scrollPrev,
  scrollNext,
  goToDot,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  measure
} = useCarouselScroll({
  itemCount: () => banners.value.length,
  cardSelector: '.banner-card'
})

// ───────────────────────────────────────────────────────────────────
// EVENT HANDLERS - BANNER CLICKS & DRAGS
// ───────────────────────────────────────────────────────────────────

/**
 * Handles banner link clicks
 * Prevents navigation if carousel was dragged
 * @param {Event} event - Click event
 * @param {Object} banner - Banner data object
 */
function handleBannerClick(event, banner) {
  // Il click dopo un trascinamento col mouse lo blocca gia' il composable.
  if (!banner.link || banner.link === '#') {
    event.preventDefault()
  }
}

// ───────────────────────────────────────────────────────────────────
// WATCHERS & LIFECYCLE - DATI
// ───────────────────────────────────────────────────────────────────

/**
 * I banner vengono mescolati al primo caricamento...
 */
onMounted(() => {
  shuffledBannersList.value = shuffleArray(originalBanners.value)
})

/**
 * ...e rimescolati quando i dati arrivano dall'API (o cambiano).
 */
watch(originalBanners, async (newBanners) => {
  shuffledBannersList.value = shuffleArray(newBanners)
  await nextTick()
  measure()
}, { deep: true })

/**
 * Quando cambia la lista mostrata, rimisura le card.
 */
watch(banners, async () => {
  await nextTick()
  measure()
})
</script>

<style scoped lang="scss">
@import '@/assets/styles/main.scss';

.section-banners {
  padding: $space-6 $space-1 $space-4 $space-1;
  background-color: $clr-bg-page;
  position: relative;
  z-index: 1;

  .container {
    max-width: 100%;
    padding-left: $space-1;
    padding-right: $space-1;
    position: relative;
    z-index: 1;
  }

  .carousel-container {
    position: relative;
    overflow: visible;
  }

  .arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 22px;
    height: 70px;
    background: rgba($clr-white, 0.65);
    border: none;
    border-radius: 6px;
    cursor: pointer;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition: all 0.3s ease;
    box-shadow: 0 4px 12px rgba($clr-brand-dark, 0.1);

    svg {
      width: 24px;
      height: 24px;
      color: $clr-brand-dark;
      flex-shrink: 0;
    }

    @media (hover: hover) {
      &:hover {
        background: rgba($clr-white, 1);
        transform: translateY(-50%) scale(1.08);
        box-shadow: 0 6px 20px rgba($clr-brand-dark, 0.15);

        svg {
          color: $clr-tertiary;
        }
      }
    }

    &:active {
      transform: translateY(-50%) scale(0.95);
    }

    &.arrow-left {
      left: 10px;
    }

    &.arrow-right {
      right: 10px;
    }
  }

  .carousel-viewport {
    position: relative;
    width: 100%;
    margin-left: 0;

    /* Scorrimento col dito = scroll NATIVO del browser (inerzia e snap di
       sistema), non simulato in JavaScript. */
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    overscroll-behavior-x: contain;
    cursor: grab;

    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }

    /* durante il trascinamento col mouse lo snap va spento, sennò scatta */
    &.is-dragging {
      scroll-snap-type: none;
      cursor: grabbing;
      user-select: none;
    }

    /* main.scss mette touch-action: pan-y su TUTTE le img sotto i 480px:
       qui dentro impedirebbe lo swipe orizzontale nativo. */
    img {
      touch-action: auto;
    }
  }

  .carousel-track {
    display: flex;
    @include flex-gap($space-2);
  }

  .banner-card {
    scroll-snap-align: start;
    flex: 0 0 calc((100% - 2 * #{$space-2}) / 3);
    min-width: calc((100% - 2 * #{$space-2}) / 3);
    max-width: calc((100% - 2 * #{$space-2}) / 3);
    aspect-ratio: 2 / 1;

    /* simulazione fedele: su Chrome aspect-ratio resta attivo anche con
       .no-aspectratio, quindi in ?legacy=1 lo si annulla per vedere la
       sola ricaduta. Su un dispositivo vecchio vero non serve. */
    .legacy-sim & { aspect-ratio: auto; }

    /* RICADUTA iOS < 15 (niente aspect-ratio).
       Senza rapporto la card non ha nessuna fonte di altezza - i figli usano
       height:100% - quindi collassa a zero e il banner sparisce.

       Il padding percentuale si calcola sulla larghezza del CONTENITORE, non
       dell'elemento: messo sulla card darebbe la larghezza del track e quindi
       un'altezza sbagliata. Va messo su .banner-link, la cui larghezza e' il
       100% della card: li' il 50% e' davvero meta' della larghezza della card,
       cioe' il rapporto 2:1.
       .banner-link e' gia' position:relative, quindi il wrapper puo'
       ancorarsi a lui. */
    .no-aspectratio & {
      .banner-link {
        height: 0;
        padding-top: 50%;
      }

      .banner-image-wrapper {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        height: auto;
      }
    }

    overflow: hidden;
    border-radius: 0;
    transition: all 0.3s ease;
    background: $clr-bg-card;

    @media (hover: hover) {
      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 32px rgba($clr-brand-dark, 0.12);
      }
    }

    .banner-link {
      display: block;
      width: 100%;
      height: 100%;
      text-decoration: none;
      position: relative;
      cursor: pointer;
    }

    .banner-image-wrapper {
      width: 100%;
      height: 100%;
      position: relative;
      overflow: hidden;

      .banner-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.3s ease;
      }

      .banner-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba($clr-brand-dark, 0.3);
        opacity: 0;
        transition: opacity 0.3s ease;
        pointer-events: none;
      }

      .banner-text {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        align-items: flex-end;
        padding: $space-2;
        opacity: 0;
        transition: opacity 0.3s ease;
        pointer-events: none;

        .banner-title {
          color: $clr-white;
          font-size: clamp(1.2rem, 3vw, 1.8rem);
          font-weight: 600;
          line-height: 1.2;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
        }
      }
    }

    &:hover {
      .banner-image {
        transform: scale(1.08);
      }

      .banner-overlay {
        opacity: 1;
      }

      .banner-text {
        opacity: 1;
      }
    }
  }

  .dots-navigation {
    display: flex;
    justify-content: center;
    @include flex-gap($space-1);
    margin-top: $space-3;

    .dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      border: none;
      background-color: rgba($clr-text-light, 0.3);
      cursor: pointer;
      transition: all 0.3s ease;
      padding: 0;

      &:hover {
        background-color: rgba($clr-text-light, 0.5);
        transform: scale(1.2);
      }

      &.active {
        background-color: $clr-tertiary;
        width: 24px;
        border-radius: 5px;
      }
    }
  }
}

@media (max-width: 1300px) {
  .section-banners {
    padding: $space-5 $space-0-5 $space-3 $space-0-5;

    .arrow {
      width: 20px;
      height: 65px;

      svg {
        width: 13px;
        height: 26px;
      }
    }

    .banner-card {
      .banner-image-wrapper {
        .banner-text {
          .banner-title {
            font-size: clamp(1.1rem, 2.8vw, 1.6rem);
          }
        }
      }
    }

    .dots-navigation {
      margin-top: $space-2-5;
    }
  }
}

@media (max-width: 1024px) {
  .section-banners {
    padding: $space-4 $space-0-5 $space-2-5 $space-0-5;

    .arrow {
      width: 20px;
      height: 60px;
      background: rgba($clr-white, 0.95);

      svg {
        width: 12px;
        height: 24px;
      }

      &.arrow-left {
        left: 10px;
      }

      &.arrow-right {
        right: 10px;
      }
    }

    .carousel-track {
      @include flex-gap($space-1-5);
    }

    .banner-card {
      flex: 0 0 calc((100% - #{$space-1-5}) / 2);
      min-width: calc((100% - #{$space-1-5}) / 2);
      max-width: calc((100% - #{$space-1-5}) / 2);

      .banner-image-wrapper {
        .banner-text {
          padding: $space-1-5;
          
          .banner-title {
            font-size: clamp(1rem, 2.5vw, 1.4rem);
          }
        }
      }
    }

    .dots-navigation {
      margin-top: $space-2;
    }
  }
}

@media (max-width: 768px) {
  .section-banners {
    padding: $space-3 $space-0-5 $space-2 $space-0-5;

    .arrow {
      width: 18px;
      height: 55px;

      svg {
        width: 11px;
        height: 22px;
      }

      &.arrow-left {
        left: 5px;
      }

      &.arrow-right {
        right: 5px;
      }
    }

    .carousel-track {
      @include flex-gap($space-1);
    }

    .banner-card {
      flex: 0 0 calc((100% - #{$space-1}) / 2);
      min-width: calc((100% - #{$space-1}) / 2);
      max-width: calc((100% - #{$space-1}) / 2);

      .banner-image-wrapper {
        .banner-text {
          padding: $space-1;
          
          .banner-title {
            font-size: clamp(0.9rem, 2.2vw, 1.2rem);
          }
        }
      }
    }

    .dots-navigation {
      margin-top: $space-1-5;
      @include flex-gap($space-0-5);

      .dot {
        width: 8px;
        height: 8px;

        &.active {
          width: 20px;
        }
      }
    }
  }
}

@media (max-width: 480px) {
  .section-banners {
    padding: $space-2-5 $space-0-5 $space-1-5 $space-0-5;

    .arrow {
      width: 16px;
      height: 50px;
      background: rgba($clr-white, 0.85);

      svg {
        width: 10px;
        height: 20px;
      }

      &.arrow-left {
        left: 10px;
      }

      &.arrow-right {
        right: 10px;
      }
    }

    .carousel-viewport {
      width: 100%;
      margin-left: 0;
    }

    .carousel-track {
      @include flex-gap(0);
      align-items: center;
    }

    .banner-card {
      flex: 0 0 100%;
      min-width: 100%;
      max-width: 100%;

      .banner-image-wrapper {
        .banner-text {
          padding: $space-0-75;
          
          .banner-title {
            font-size: clamp(0.85rem, 4vw, 1.1rem);
          }
        }
      }
    }

    .dots-navigation {
      margin-top: $space-1;

      .dot {
        width: 6px;
        height: 6px;

        &.active {
          width: 16px;
        }
      }
    }
  }
}

@media (max-width: 360px) {
  .section-banners {
    padding: $space-2 $space-0-5 $space-1 $space-0-5;

    .arrow {
      width: 35px;
      height: 35px;

      svg {
        width: 18px;
        height: 18px;
      }

      &.arrow-left {
        left: 5px;
      }

      &.arrow-right {
        right: 5px;
      }
    }

    .banner-card {
      .banner-image-wrapper {
        .banner-text {
          padding: $space-0-5;
          
          .banner-title {
            font-size: clamp(0.8rem, 3.5vw, 1rem);
          }
        }
      }
    }

    .dots-navigation {
      margin-top: $space-0-75;

      .dot {
        width: 5px;
        height: 5px;

        &.active {
          width: 14px;
        }
      }
    }
  }
}
</style>